import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Control,
  DeepPartialSkipArrayKey,
  FieldPath,
  FieldPathValues,
  FieldValues,
  useWatch,
} from "react-hook-form";

/**
 * Wraps react-hook-form's useWatch hook with a debounce for changes to the form fields.
 *
 * @example
 * ```tsx
 * const { control } = useForm();
 * const [schedule, activity] = useWatchDebounced({
 *   name: ["schedule", "activity"],
 *   control,
 * })
 *
 * useEffect(() => {
 *   // logic here is debounced
 * }, [schedule, activity]);
 * ```
 */
export function useWatchDebounced<
  TFieldValues extends FieldValues = FieldValues,
  TFieldNames extends
    readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
>(
  props: {
    name: readonly [...TFieldNames];
    defaultValue?: DeepPartialSkipArrayKey<TFieldValues>;
    control?: Control<TFieldValues>;
    disabled?: boolean;
    exact?: boolean;
  },
  delay: number = 500,
): FieldPathValues<TFieldValues, TFieldNames> {
  const watchedFields = useWatch(props) as FieldPathValues<
    TFieldValues,
    TFieldNames
  >;
  const [debouncedFieldValues, setDebouncedFieldValues] =
    useState(watchedFields);

  const previousFieldValues = useRef(watchedFields);

  const debounced = useMemo(() => {
    return debounce((newValues: FieldPathValues<TFieldValues, TFieldNames>) => {
      if (isEqual(previousFieldValues.current, newValues)) {
        return;
      }

      previousFieldValues.current = newValues;
      setDebouncedFieldValues(newValues);
    }, delay);
  }, [delay]);

  useEffect(() => {
    debounced(watchedFields);

    return () => {
      debounced.cancel(); // Cancel any pending debounced calls on cleanup
    };
  }, [watchedFields, debounced]);

  return debouncedFieldValues;
}
